import { effect, EventEmitter, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, from, Observable, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { noPermissions, RoleDefinition } from './role-model';
import Keycloak from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEvent, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular';
import {
	AuthProvider, ConnectionService, StorageService, ServerBlockService, OfflineModeService,
	anmeldungWurdeAbgelehnt, gespeicherterSchluesselAbgelaufen, mitZeitgrenze, ZEITGRENZE,
	ConsoleErrorService,
} from '@luzula/luzula-lib';

interface AuthImpl {
	init(): Promise<void>;
	/**
	 * Kommt der Nutzer gerade von der Anmeldung zurueck?
	 *
	 * Keycloak haengt seinen Rueckruf an die Adresse (`?code=…&state=…`).
	 * Eingeloest wird der ausschliesslich von `keycloak.init()` — deshalb muss
	 * der Offline-Pfad ihn erkennen koennen (siehe {@link AuthService.init}).
	 *
	 * Freiwillig: Meldet ein Unterbau es nicht, bleibt es beim alten Verhalten.
	 */
	hatAnmeldeRueckruf?(): boolean;
	/**
	 * Wartet auf das ECHTE Ende von `keycloak.init()`.
	 *
	 * `init()` gibt ohne Rueckruf-Adresse sofort frei, damit der App-Start nicht
	 * an einem haengenden Anmelde-Server haengt; das eigentliche `keycloak.init()`
	 * laeuft dann noch. Wer danach die Anmeldung starten will (`relogin`), muss
	 * hierauf warten, sonst startet sie mitten in ein laufendes `init()` hinein
	 * (S12, Nutzer-Befund 17.09.2026: Anmeldung in der App nie angekommen).
	 *
	 * Freiwillig: Ein Unterbau ohne die Methode gilt als sofort fertig.
	 */
	initAbgeschlossen?(): Promise<void>;
	login$(): Observable<void>;
	logout$(): Observable<void>;

	user$(): Observable<any | null | undefined>;

	isAuthenticated$(): Observable<boolean>;
	isAuthenticated(): boolean;

	access(): Observable<any>;

	getKeycloak(): Keycloak;
	notifyAuthenticated(value: boolean): void;
}

interface AuthInfo {
	roles: string[];
	realmRoles: string[];
	matrix: object;
	currentUser: string;
	currentUsername: string;
	currentUserId: string;
	currentRole: string;
}

export class KeycloakImpl implements AuthImpl {
	private readonly impl = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
	private readonly storage = inject(StorageService);
	/**
	 * Dahin geht die Meldung „der Anmelde-Server hat den Schluessel abgelehnt".
	 *
	 * Freiwillig, weil reine Logik-Specs den Barrel nachbauen und den Dienst
	 * nicht bereitstellen. Er haengt an keinem Anmelde-Dienst, es entsteht also
	 * kein Ringschluss in der Verdrahtung.
	 */
	private readonly serverBlock = inject(ServerBlockService, { optional: true });
	/**
	 * Trichter fuer Fehler, die wir wissen wollen, ohne den Start zu sprengen.
	 * Freiwillig aus demselben Grund wie `serverBlock` — Logik-Specs bauen den
	 * Barrel nach und stellen den Dienst nicht bereit.
	 */
	private readonly ces = inject(ConsoleErrorService, { optional: true });
  authenticated$ = new BehaviorSubject<boolean>(false);
  keycloakStatus: string | undefined;
  private initialized = false;

  constructor() {
    effect(() => this.handleKeycloakEvent(this.keycloakSignal()));
  }

  /**
   * Verarbeitet Keycloak-Lifecycle-Events.
   *
   * Wichtig für Offline-First: Ein gescheiterter SSO-Check (z.B. nach Tagen
   * offline, wenn der Server nicht erreichbar ist oder der Refresh-Token
   * abgelaufen ist) feuert `Ready` mit `auth=false`. In diesem Fall dürfen
   * gespeicherte Tokens NICHT entfernt werden — sonst geht der Offline-Restore
   * via cached `ai`-AuthInfo zwar noch (solange `ai` lebt), aber ein späterer
   * Online-Versuch hat keinen Refresh-Token mehr. Tokens nur bei einem
   * expliziten `AuthLogout`-Event löschen.
   */
  handleKeycloakEvent(keycloakEvent: KeycloakEvent): void {
    this.keycloakStatus = keycloakEvent.type;
    if (keycloakEvent.type === KeycloakEventType.Ready) {
      const auth = typeEventArgs<ReadyArgs>(keycloakEvent.args);
      if (auth) {
        if (this.impl.token)
          this.storage.setSync('kc-token', this.impl.token);
        if (this.impl.refreshToken)
          this.storage.setSync('kc-refresh-token', this.impl.refreshToken);
      }
      this.authenticated$.next(auth);
    }

    if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
      this.storage.removeSync('kc-token');
      this.storage.removeSync('kc-refresh-token');
      this.authenticated$.next(false);
    }
  }
	
	/**
	 * Initialisiert keycloak-js. Gibt eine Promise zurück, die der
	 * App-Initializer abwarten kann/muss.
	 *
	 * Callback-aware: Liegt ein Redirect-Callback (`?code&state`) in der URL,
	 * MUSS der App-Start auf `keycloak.init()` warten — sonst verarbeitet der
	 * Angular-Router die URL und verwirft die Callback-Parameter, bevor
	 * keycloak-js den Code gegen ein Token tauschen kann (Folge: Fallback auf
	 * den Silent-SSO-iframe, der bei blockierten 3rd-Party-Cookies in
	 * "Timeout when waiting for 3rd party check iframe message." läuft).
	 *
	 * Ohne Callback (anonymer/Offline-Kaltstart) NICHT blockieren: der
	 * Silent-SSO-Check läuft im Hintergrund weiter, ein Timeout darf den
	 * App-Start aber nicht ~10s aufhalten.
	 */
	/**
	 * Das echte Ende von `keycloak.init()`, unabhaengig davon, ob {@link init}
	 * darauf gewartet hat. Loest immer auf, auch wenn `keycloak.init()`
	 * gescheitert ist (der Fehler ist dann schon behandelt).
	 */
	private initFertig: Promise<void> = Promise.resolve();

	initAbgeschlossen(): Promise<void> {
		return this.initFertig;
	}

	init(): Promise<void> {
		if (this.initialized) return Promise.resolve();
		this.initialized = true;

		const hasCallback = this.hasRedirectCallback();

		const initPromise = Promise.resolve(
			this.impl.init(
				{
					// KEIN check-sso (onLoad weggelassen): Der Silent-SSO-iframe ist
					// cross-site (App ↔ id.waldkonzepte.de) und laeuft bei geblockten
					// 3rd-Party-Cookies in "Timeout when waiting for 3rd party check
					// iframe message" — das verzoegerte jeden Start und verhinderte
					// (bei Login-Callback) den Code-Tausch (Re-Login-Loop).
					// Stattdessen:
					//  - gespeicherten Token adoptieren (token/refreshToken unten) und
					//    bei Bedarf via reauthInterceptor/updateToken refreshen,
					//  - einen Login-Callback (#code) verarbeitet init() auch ohne
					//    onLoad,
					//  - SSO-Wiederanmeldung ohne gespeicherten Token erfolgt durch
					//    expliziten Login-Klick (voller Redirect, 1st-party).
					redirectUri: window.location.origin + '/',
					...this.gespeicherteSchluessel(),
					// Session-Status-iframe ebenfalls aus (gleicher 3rd-Party-Cookie-Grund).
					checkLoginIframe: false,
				}
			)
		).then(() => undefined).catch(err => {
			// keycloak.init() darf den App-Start NICHT sprengen — Auth bleibt
			// schlicht false (Offline-First / expliziter Login).
			// Die App laeuft bewusst ohne Anmeldung weiter (Offline-First). Genau
			// daraus entstand die Kette „App sieht angemeldet aus, speichert aber
			// nirgends hin" — der Ausfall selbst soll deshalb nicht stumm bleiben.
			console.warn('[Auth] keycloak.init() fehlgeschlagen:', err);
			this.ces?.melde('[Anmeldung] keycloak.init() fehlgeschlagen', err);
			// Den gespeicherten Schluessel NUR wegwerfen, wenn der Anmelde-Server
			// ihn wirklich ABGELEHNT hat.
			//
			// Bis 08.09.2026 reichte hier `navigator.onLine` — jeder Fehlschlag
			// galt als Ablehnung. Das ist falsch: Derselbe Aufruf scheitert auch,
			// wenn der Anmelde-Server gar nicht erreicht wurde, und
			// `navigator.onLine` kennt nur die Netzwerkkarte, nicht die Leitung.
			// Ein Wackler beim Start kostete so die Anmeldung dauerhaft; danach
			// lief die App ohne Schluessel weiter und gab beim Speichern ein 405
			// zurueck (Nutzer-Befund, Tablet).
			if (anmeldungWurdeAbgelehnt(err)) {
				this.storage.removeSync('kc-token');
				this.storage.removeSync('kc-refresh-token');
				// Und es SAGEN (Nutzer-Befund 10.09.2026, bpweb von Produktion):
				// Bis dahin verschwand der Schluessel still, und der Nutzer sass
				// eine knappe Minute vor dem Startbildschirm. Die Wache der
				// Bibliothek sieht nur alle 30 Sekunden nach — beim Start mit
				// Absicht gar nicht, denn da laeuft die Anmeldung gerade erst an —
				// und fasste danach gut 31 Sekunden geduldig an genau diesem
				// Schluessel nach. Hier steht die Antwort des Servers schon fest;
				// wer sie kennt, gibt sie weiter.
				this.serverBlock?.merkeAnmeldungAbgelehnt();
			}
		});
		this.initFertig = initPromise;

		return hasCallback ? initPromise : Promise.resolve();
	}

	/**
	 * Die gespeicherten Schluessel fuer `keycloak.init()` — oder keine, wenn der
	 * Erneuerungs-Schluessel laut eigenem Ablaufdatum laengst vorbei ist.
	 *
	 * Nutzer-Befund 14.09.2026 (bpweb von Produktion): „ich starte bpweb und es
	 * bleibt haengen. nach refresh startet es schnell." Der Start hing am
	 * Startbildschirm. Der gespeicherte Schluessel war laengst abgelaufen, ging
	 * aber ungeprueft zur Erneuerung an den Anmelde-Server; die ersten
	 * Datenbank-Anfragen liefen mit demselben toten Schluessel los, wurden
	 * abgewiesen und warteten dann auf genau diese Erneuerung. Antwortete der
	 * Anmelde-Server langsam, wartete der ganze Start mit. Nach dem Neuladen war
	 * der Schluessel weg — und alles ging sofort.
	 *
	 * Dabei steht das Ablaufdatum im Schluessel selbst. Was die Uhr schon
	 * weiss, muss niemand den Server fragen: Der Schluessel wird sofort
	 * weggeworfen, der Balken „Anmeldung abgelaufen" gleich aufgestellt (die
	 * Wache der Bibliothek hoert auf `merkeAnmeldungAbgelehnt`), und die App
	 * startet ohne Umweg. Die Regel selbst steht in der Bibliothek
	 * (`gespeicherterSchluesselAbgelaufen`) und sagt nur dann „vorbei", wenn es
	 * sicher ist — im Zweifel entscheidet wie bisher der Server.
	 */
	private gespeicherteSchluessel(): { token?: string; refreshToken?: string } {
		const token = this.storage.getSync('kc-token') || undefined;
		const refreshToken = this.storage.getSync('kc-refresh-token') || undefined;
		if (!gespeicherterSchluesselAbgelaufen(refreshToken)) return { token, refreshToken };

		this.storage.removeSync('kc-token');
		this.storage.removeSync('kc-refresh-token');
		this.serverBlock?.merkeAnmeldungAbgelehnt();
		return {};
	}

	/**
	 * Kommt der Nutzer gerade von der Anmeldung zurück?
	 *
	 * Öffentlich, weil auch der Offline-Pfad des {@link AuthService} das wissen
	 * muss: Dort wird `keycloak.init()` sonst gar nicht erst gerufen, und ein
	 * Rückruf bliebe für immer liegen (Nutzer-Befund 08.09.2026).
	 */
	hatAnmeldeRueckruf(): boolean {
		return this.hasRedirectCallback();
	}

	/** True wenn die URL einen Keycloak-Redirect-Callback enthält. */
	private hasRedirectCallback(): boolean {
		const qs = window.location.search + window.location.hash;
		return /[?&#](code|state|session_state|error)=/.test(qs);
	}
	
	user$() {
		return of(this.impl.tokenParsed);
	}
	
	login$() {
		return from(this.impl.login());
	}
	
	logout$() {
		this.storage.removeSync('kc-token');
		this.storage.removeSync('kc-refresh-token');
		return from(this.impl.logout());
	}
	
	isAuthenticated$() {
		return this.authenticated$;
	}
	
	isAuthenticated(): boolean {
		return !!this.impl.authenticated;
	}
	
	access() {
		return of({ realm: this.impl.realmAccess, resources: this.impl.resourceAccess });
	}

	getKeycloak(): Keycloak {
		return this.impl;
	}

	notifyAuthenticated(value: boolean): void {
		this.authenticated$.next(value);
	}
}


@Injectable({
	providedIn: 'root'
})
export class AuthService implements AuthProvider {
	authImpl: AuthImpl = new KeycloakImpl();
	
	commenting = false;
	pen: 'danger'|'warning'|'message'|'rubber' = 'danger';
	currentRole = '';

	/**
	 * Rollen-Mapping fuer die Umbenennungen (Phase 0 + Phase B):
	 *  - `kartierer` -> `bearbeiter`
	 *  - `gast`      -> `datennutzer`
	 * Keycloak-Token (`role:kartierer`/`role:gast`) und gecachte AuthInfo tragen
	 * weiterhin die Alt-Namen; hier wird beim Einlesen auf den neuen Identifier
	 * normalisiert, damit `canIbe('bearbeiter')`/`canIbe('datennutzer')` greifen.
	 *
	 * BLEIBT DAUERHAFT (User-Entscheidung 2026-05-22): Der Keycloak-Realm wird
	 * NICHT migriert — stattdessen wird die vorhandene Rolle hier gemappt. Dieses
	 * Mapping daher NICHT entfernen; sonst verlieren Bestandsnutzer mit
	 * `kartierer`/`gast`-Rolle ihre Berechtigungen.
	 */
	private normalizeRole(role: string): string {
		if (role === 'kartierer') return 'bearbeiter';
		if (role === 'gast') return 'datennutzer';
		return role;
	}
	public currentUser = '';
	/**
	 * Login-Benutzername aus dem Keycloak-Token (`preferred_username`, z.B.
	 * „mpohlers"). NICHT identisch mit `currentUser` (= Anzeigename `name`).
	 * Für das Einladungs-Matching per Benutzername (Phase K) — neben E-Mail und
	 * Anzeigename ein weiterer Treffer-Alias.
	 */
	public currentUsername = '';
	/** E-Mail aus dem Keycloak-Token (`email`). Für das Einladungs-Matching (Phase K). */
	public currentUserEmail = '';

	// wraps logic about being authenticated in an offline fassion
	public isAuthenticated = new BehaviorSubject<boolean>(false);

	/**
	 * Die Angaben aus dem Anmelde-Schluessel — `undefined`, wenn gerade keiner
	 * vorliegt (offline, oder der Server hat ihn abgelehnt).
	 *
	 * Wichtig ist nicht der Inhalt, sondern das schlichte „ist einer da?": Wird
	 * der gespeicherte Schluessel abgelehnt, wirft die App ihn weg und laeuft
	 * ohne Anmeldung weiter. Name und Rolle zeigt sie dabei weiter, denn die
	 * kommen aus dem Zwischenspeicher — es sieht also aus wie angemeldet, und
	 * der Ausfall zeigt sich erst beim Speichern (Nutzer-Befund 01.09.2026).
	 * Daran haengt die Wache des Balkens „Anmeldung abgelaufen"
	 * (`wd-sitzungs-hinweis` aus der Bibliothek).
	 */
	tokenClaims(): Record<string, any> | undefined {
		// Ueber `authImpl` — `impl` gehoert zu `KeycloakImpl` und gibt es in
		// dieser Klasse nicht. Bis 04.09.2026 stand hier `this.impl`, sodass jeder
		// Aufruf in einen Fehler lief und das Warnzeichen nie erscheinen konnte.
		// Das `?.` deckt den Offline-Start ab: Dort lief `keycloak.init()` nie,
		// und der interne Adapter ist noch leer.
		return this.authImpl.getKeycloak()?.tokenParsed as Record<string, any> | undefined;
	}

	/**
	 * Gruppenpfade des Nutzers aus dem Token-`groups`-Claim (feature/user-groups).
	 * Damit stempelt die Bibliothek neu angelegte Projekte (und darueber deren Dokumente)
	 * mit den Gruppen des Erstellers — base-project.service `create()` liest genau diese
	 * Methode. Ohne Token (offline / nicht angemeldet) oder ohne Claim: leer.
	 */
	getGroups(): string[] {
		const g = this.tokenClaims()?.['groups'];
		return Array.isArray(g) ? g.filter((x): x is string => typeof x === 'string') : [];
	}

	private isOnline: boolean = navigator.onLine;
	
	public loggedOut = new EventEmitter<void>;
	public loggedIn = new EventEmitter<void>;
	public loginFailed = new EventEmitter<void>;
	/**
	 * Reaktives Auth-Ready-Signal: true, sobald ein echter Keycloak-Login mit
	 * gueltigem Bearer-Token vorliegt (nicht schon beim localStorage-Cache-Boot).
	 * base-pouchdb-Services (DataService/ProjectService) gaten damit ihre
	 * token-abhaengige Remote-Init (Design-Docs, Seeding), sodass sie nie im
	 * Pre-Token-Fenster laufen und ausgeloggt (Read-Only) uebersprungen werden.
	 */
	public readonly authReady = signal(false);
	public authChanged = new EventEmitter<void>;
	
	private matrix = noPermissions;
	private roles = new Set<string>;
	private realmRoles = new Set<string>;
	public currentUserId = '';
	private authSub?: Subscription;

	private cs = inject(ConnectionService);
	private storage = inject(StorageService);
	// Optional: in reinen Logik-Specs (vi.mock-Barrel) nicht bereitgestellt → null.
	private serverBlock = inject(ServerBlockService, { optional: true });
	private oms = inject(OfflineModeService, { optional: true });
	/** onTokenExpired wird nur einmal verdrahtet (init kann mehrfach durch den Monitor laufen). */
	private tokenExpiryWired = false;

	constructor() {
		this.loggedIn.subscribe(() => this.authChanged.emit());
		this.loggedOut.subscribe(() => this.authChanged.emit());
		this.loginFailed.subscribe(() => this.authChanged.emit());

		// Verlässt der Nutzer den Offline-Modus, nach einem Offline-Start die
		// Online-Initialisierung nachholen (keycloak.init() lief beim Offline-Start
		// nie → interner Adapter undefined). Nur wenn wir wirklich offline waren
		// (`isOnline === false`) — auf einem normalen Online-Start ist nichts
		// nachzuholen, und die Start-Emission des Offline-Signals würde sonst
		// unnötig ein zweites checkAuthentication() auslösen.
		this.oms?.offlineModeChanged?.subscribe((offline: boolean) => {
			if (!offline && !this.isOnline) this.resumeOnline();
		});
	}

	/**
	 * Globale Capability-Matrix aus ALLEN zugewiesenen Rollen (Multi-Role-Union,
	 * User-Entscheidung 2026-06-12). Vereinigt die `RoleDefinition`-Capabilities
	 * jeder Rolle in `this.roles` plus den `datennutzer`-Floor (read:points für
	 * jeden authentifizierten Nutzer). Ersetzt das alte `bestRole()`-Konzept, das
	 * nur EINE (höchstrangige) Rolle anwandte und damit z.B. einem
	 * `manager`+`bearbeiter`-User das `add:points` der Bearbeiter-Rolle nahm.
	 * Unbekannte Rollen (ohne `RoleDefinition`-Eintrag, z.B. `admin`) werden
	 * still ignoriert.
	 */
	private buildRoleMatrix(): Map<string, Set<string>> {
		const union = new Map<string, Set<string>>();
		const addFrom = (roleMatrix: Map<string, Set<string>> | undefined) => {
			if (!roleMatrix) return;
			for (const [resource, accesses] of roleMatrix) {
				if (!union.has(resource)) union.set(resource, new Set<string>());
				for (const a of accesses) union.get(resource)!.add(a);
			}
		};
		// datennutzer-Floor: jeder authentifizierte Nutzer darf Punkte lesen.
		addFrom(RoleDefinition.get('datennutzer'));
		for (const role of this.roles) addFrom(RoleDefinition.get(role));
		return union;
	}

	private storeAuthInfo() {
		const inf: any = {
			currentUser: this.currentUser,
			currentUsername: this.currentUsername,
			currentUserId: this.currentUserId,
			currentUserEmail: this.currentUserEmail,
			roles: [...this.roles],
			realmRoles: [...this.realmRoles],
			matrix: { } as any
		};
		for (const k of this.matrix?.keys()) {
			inf.matrix[k] = [...this.matrix.get(k)!];
		}
		this.storage.setSync('ai', btoa(JSON.stringify(inf)));
	}

	private retrieveAuthInfo(emitChange = true): boolean {
		const inf = this.storage.getSync('ai');
		let success = false;
		if (inf) {
			try {
				const decoded = atob(inf);
				const parsed = JSON.parse(decoded);
				this.currentUser = parsed.currentUser;
				this.currentUsername = parsed.currentUsername || '';
				this.currentUserId = parsed.currentUserId || '';
				this.currentUserEmail = parsed.currentUserEmail || '';
				// parsed.currentRole wird ignoriert (Konzept entfallen) — Alt-Daten
				// werden silent durchgereicht.
				this.roles = new Set((parsed.roles as string[]).map(r => this.normalizeRole(r)));
				this.realmRoles = new Set(parsed.realmRoles || []);
				this.matrix = new Map<string,Set<string>>;
				for (const k of Object.keys(parsed.matrix))
					this.matrix.set(k, new Set<string>(parsed.matrix[k]));
				success = true;
			} catch (e) {
				console.warn('Failed to parse auth info:', e);
			}
		}
		if (emitChange) this.authChanged.emit();
		return success;
	}
	
	public init(online: boolean = navigator.onLine, onAppLoad = false): Promise<void> {
		if (!onAppLoad) return Promise.resolve();

		// Wenn bereits bekannt ist dass wir offline sind (z.B. Offline-Modus),
		// sofort initialisieren ohne auf Netzwerk-Check zu warten
		if (!online) {
			this.isOnline = false;
			// AUSNAHME: ein Anmelde-Rueckruf muss trotzdem eingeloest werden.
			//
			// Nutzer-Befund 08.09.2026 (Tablet, 17 Bohrpunkte im Gelaende): Der
			// Sitzungs-Balken meldete richtig, dass die Anmeldung weg ist, und bot
			// „Neu anmelden" an. Der Nutzer meldete sich an, kam zurueck — und der
			// Balken stand sofort wieder da. Viermal.
			//
			// Keycloak haengt seinen Rueckruf an die Adresse (`?code=…&state=…`),
			// und eingeloest wird der ausschliesslich von `keycloak.init()`. Das
			// lief hier nie. Am Geraet gemessen: kein `kc-token`, dafuer VIER
			// liegengebliebene `kc-callback-*`-Eintraege — einer je Anlauf.
			//
			// Die Abkuerzung bleibt fuer den gewoehnlichen Offline-Start richtig:
			// Ohne Netz soll die App nicht auf einen Anmelde-Server warten. Falsch
			// ist sie nur, wenn gerade jemand von der Anmeldung zurueckkommt — und
			// genau das steht in der Adresse.
			if (this.authImpl.hatAnmeldeRueckruf?.()) {
				return Promise.resolve(this.authImpl.init())
					// Der Anmelde-Server kann trotzdem unerreichbar sein. Dann startet
					// die App eben ohne Anmeldung weiter — aber sie startet.
					.catch(err => { console.warn('[Auth] Anmelde-Rueckruf nicht einloesbar:', err); })
					.then(() => {
						this.wireTokenExpiry();
						// Emit=false: checkAuthentication() emittiert authChanged selbst
						this.retrieveAuthInfo(false);
						this.checkAuthentication();
					});
			}
			// Emit=false: checkAuthentication() emittiert authChanged selbst
			this.retrieveAuthInfo(false);
			this.checkAuthentication();
			return Promise.resolve();
		}

		// Online-Pfad: keycloak.init() MUSS abgewartet werden, BEVOR der
		// App-Initializer durchläuft (sonst frisst der Router den Redirect-
		// Callback). Die zurückgegebene Promise resolved nach dem ersten
		// Monitor-Zyklus; die Subscription bleibt für spätere
		// Online/Offline-Wechsel bestehen (unverändertes Laufzeit-Verhalten).
		return new Promise<void>(resolve => {
			let firstDone = false;
			this.cs.monitor().subscribe(async (o: boolean) => {
				this.isOnline = o;
				// Emit=false: checkAuthentication() emittiert authChanged selbst
				this.retrieveAuthInfo(false);
				if (o) { await this.authImpl.init(); this.wireTokenExpiry(); }
				this.checkAuthentication();
				if (!firstDone) { firstDone = true; resolve(); }
			});
		});
	}
	
	private checkAuthentication() {
		if (this.isOnline) {
			// Vorherige Subscription aufräumen um Race-Conditions zu vermeiden
			this.authSub?.unsubscribe();
			this.authSub = this.authImpl.isAuthenticated$().subscribe((authenticated: boolean) => {
				if (authenticated) {
					this.authImpl.user$().subscribe((user: any) => {
						if (user) {
							// Härtung (Bug 1, 2026-06-11): Gehört der via retrieveAuthInfo
							// optimistisch restaurierte Cache einem ANDEREN User als dem
							// frischen Token, wird er komplett verworfen — nie gemerged.
							// (Tablet-Szenario: ai einer Super-User-Session überlebt
							// Session-Ablauf/App-Kill und würde sonst den nächsten User
							// kontaminieren.)
							if (this.currentUserId && this.currentUserId !== user.sub) {
								this.storage.removeSync('ai');
								this.matrix = noPermissions;
							}
							this.currentUser = user.name;
							this.currentUsername = user.preferred_username || '';
							this.currentUserId = user.sub;
							this.currentUserEmail = user.email || '';
							this.authImpl.access().subscribe((access: any) => {
								// Token-Treue (Bug 1, 2026-06-11): Das frische Token ist die
								// EINZIGE Rollenquelle. Sets werden hier NEU aufgebaut statt
								// additiv befüllt — sonst akkumulieren Rollen über Sessions/
								// User (ai-Cache + In-Memory) und ein reiner Bearbeiter erbt
								// den Entwickler-Sicht-Bypass (getWorkflowRole → 'admin').
								// Der ai-Cache bleibt ausschließlich Offline-Fallback
								// (checkOfflineAuthentication).
								const freshRealmRoles = new Set<string>();
								(access?.realm?.roles as string[])?.forEach((r: string) => freshRealmRoles.add(r));
								this.realmRoles = freshRealmRoles;

								const freshRoles = new Set<string>();
								(access?.resources?.bpweb?.roles as string[])?.forEach((p: string) => {
									if (p.startsWith('role:'))
										freshRoles.add(this.normalizeRole(p.substring(5)));
								});
								this.roles = freshRoles;

								// Fallback: jeder angemeldete User bekommt mindestens die
								// Rolle 'datennutzer' (read-only Punkte) als Boden, auch wenn
								// Keycloak (noch) keine Client-/Realm-Rolle liefert. Hält
								// `isDatennutzer()` (roles-Set-basiert) korrekt.
								if (this.roles.size === 0) this.roles.add('datennutzer');

								// Multi-Role-Union (2026-06-12): Matrix = Vereinigung ALLER
								// zugewiesenen Rollen, nicht mehr eine per bestRole gewählte
								// Einzelrolle. currentRole-Konzept entfällt (Interface: '').
								this.applyRoleCapabilities();
							});
						}
					});
				}
				else {
					this.checkOfflineAuthentication();
				}
			});
		}
		else {
			this.checkOfflineAuthentication();
		}
	}
	
	private checkOfflineAuthentication() {
		this.isAuthenticated.next(this.retrieveAuthInfo());
	}
	
	isAuthenticated$(): Observable<boolean> {
		if (this.isOnline)
			return of(this.authImpl.isAuthenticated());
		return this.isAuthenticated;
	}
	
	tryAuthenticate() {
		return this.authImpl.isAuthenticated$();
	}
	
	public onLoggedOut() {
		// `ai`-Cache verwerfen, damit nach Re-Login die Rollen ausschließlich aus
		// dem frischen Token kommen (Token-Treue, Bug 1) und die Capability-Matrix
		// neu aus der Union der Rollen gebaut wird (applyRoleCapabilities).
		this.storage.removeSync('ai');
		this.loggedOut.emit();
	}
	
	public user() {
		return this.authImpl.isAuthenticated() 
			? this.authImpl.user$() 
			: of({name: this.currentUser });
	}
	
	public login() {
		// Nach einem Offline-Start ist keycloak evtl. nicht initialisiert (interner
		// Adapter undefined). Erst (idempotent) initialisieren, DANN den Login
		// starten — sonst „Cannot read properties of undefined (reading 'login')".
		// Und auf das ECHTE Ende von init() warten, aus demselben Grund wie in
		// `relogin` (S12): Der Weg ueber das Profil-Menue ist derselbe Sprung.
		return from(
			Promise.resolve(this.authImpl.init()).then(() => this.authImpl.initAbgeschlossen?.()),
		).pipe(
			switchMap(() => this.authImpl.login$()),
		);
	}

	/**
	 * Frische interaktive Anmeldung zur 401-Wiederherstellung. Loescht vorher die
	 * (abgelaufenen) lokalen Tokens, damit keycloak.init() beim Rueckkehr-Callback
	 * keinen Alt-Token adoptiert, und startet den vollen Login-Redirect (1st-Party
	 * bei id.waldkonzepte.de — die SSO-Session ist dort erreichbar, anders als im
	 * geblockten Silent-SSO-iframe). Den Callback verarbeitet init() dann ohne
	 * check-sso (siehe KeycloakImpl.init).
	 */
	relogin(): void {
		this.storage.removeSync('kc-token');
		this.storage.removeSync('kc-refresh-token');
		// Nach einem Offline-Start wurde keycloak.init() nie aufgerufen; der interne
		// keycloak-js-Adapter ist dann undefined und getKeycloak().login() würde mit
		// „Cannot read properties of undefined (reading 'login')" abstürzen. Erst
		// (idempotent) initialisieren, DANN den Login-Redirect starten. Die Tokens
		// sind oben schon entfernt, damit init() keinen Alt-Token adoptiert.
		//
		// Und auf das ECHTE Ende von init() warten (S12, 18.09.2026): `init()`
		// gibt ohne Rueckruf-Adresse sofort frei, waehrend keycloak.init() noch
		// laeuft. Eine Anmeldung, die mitten hinein startet, kam am 17.09.2026 in
		// der App nie an (unverbrauchter Rueckruf-Eintrag, Fenster als Ruecksprung
		// neu geladen, keine Anmeldeseite im Verlauf).
		void Promise.resolve(this.authImpl.init())
			.then(() => this.authImpl.initAbgeschlossen?.())
			.then(() => this.authImpl.getKeycloak().login());
	}

	/**
	 * Sieht das **Gerät** ein Netz?
	 *
	 * `isOnline` ist die zuletzt gemessene Lage der laufenden Verbindungsprüfung.
	 * Die ruht im Offline-Modus mit Absicht, und nach einem Start im
	 * Offline-Modus wurde sie nie gemessen — der Wert steht dort dauerhaft auf
	 * „kein Netz". Als Riegel vor einer Anfrage taugt er deshalb nicht. Der
	 * Empfang des Geräts kostet nichts und ist immer aktuell.
	 *
	 * Beide zusammen, weil `isOnline` mehr weiß als der Empfang: Es hat den
	 * Server wirklich erreicht. Der Empfang allein sagt nur, dass eine
	 * Netzwerkkarte verbunden ist.
	 */
	private siehtNetz(): boolean {
		return this.isOnline || navigator.onLine;
	}

	/**
	 * Den Anmelde-Unterbau bereitstellen — notfalls erst hochfahren.
	 *
	 * Nach einem Start im Offline-Modus lief `keycloak.init()` nie; der interne
	 * Adapter ist dann leer, und jede Erneuerung liefe ins Leere.
	 * {@link KeycloakImpl.init} ist idempotent (beim zweiten Mal ein No-Op),
	 * kostet also nichts, wenn der Unterbau längst steht.
	 *
	 * Gibt `false`, wenn er nicht hochkommt: Dann ist der Anmelde-Server nicht
	 * erreichbar, und es gibt auch keinen Schlüssel.
	 */
	private async anmeldeUnterbauBereit(): Promise<boolean> {
		try {
			await Promise.resolve(this.authImpl.init());
			this.wireTokenExpiry();
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Versucht still (ohne UI/Redirect) einen Token-Refresh via
	 * `keycloak.updateToken()`. Wird vom 401-Recovery (`reauthInterceptor` in der
	 * Lib) genutzt, um bei abgelaufenem Token den Request transparent zu
	 * wiederholen, bevor das „Sitzung abgelaufen"-Banner erscheint — und von der
	 * Sitzungs-Wache der Lib, bevor sie den Balken aufstellt.
	 *
	 * **Der Riegel hängt am Empfang des Geräts, nicht am Offline-Modus**
	 * (Nutzer-Befund 09.09.2026): „das neuanmelden-banner kommt jetzt öfter. neu
	 * anmelden geht dann auch einfach mit klick auf ‚neu anmelden' aber still
	 * wäre schöner, wenn sowieso kein passwort abgefragt wird." Genau das ist der
	 * Beweis: Verlangt der Knopf kein Passwort, steht die Sitzung beim
	 * Anmelde-Server noch, und `updateToken` wäre durchgelaufen. Gefragt wurde
	 * nur nie — hier stand `if (!this.isOnline) return false;`, und `isOnline`
	 * ist im Offline-Modus dauerhaft `false`. Die ganze Kette Wache →
	 * `refreshNachAufwachen()` → `ensureFreshToken()` endete damit in der ersten
	 * Zeile, ohne den Anmelde-Server ein einziges Mal zu fragen.
	 *
	 * Liefert false, wenn das Gerät kein Netz sieht, der Anmelde-Unterbau nicht
	 * hochkommt, kein (Refresh-)Token vorhanden ist oder der Refresh scheitert
	 * (z.B. nach fehlgeschlagenem Silent-SSO) — dann übernimmt das Banner.
	 */
	async tryRefreshToken(): Promise<boolean> {
		if (!this.siehtNetz()) return false;
		if (!await this.anmeldeUnterbauBereit()) return false;
		try {
			const kc = this.authImpl.getKeycloak();
			// minValidity in Sekunden: erneuert, wenn der Token in <30s abläuft
			// (oder bereits abgelaufen ist). Wirft, wenn kein Refresh-Token da ist.
			//
			// Gespiegelt wird an der Erneuerung selbst, nicht erst nach dem Warten:
			// Kommt die Antwort erst NACH der Zeitgrenze, soll der nächste Start
			// trotzdem den frischen Schlüssel haben — sonst finge er wieder mit dem
			// alten an (updateToken feuert kein Ready-Event).
			const erneuerung = kc.updateToken(30).then(ok => { this.spiegleSchluessel(kc); return ok; });
			// Nicht ohne Grenze warten (Nutzer-Befund 14.09.2026): Beim Start hängen
			// an dieser Erneuerung alle Datenbank-Anfragen, die mit dem alten
			// Schlüssel abgewiesen wurden — und damit der Startbildschirm. Antwortet
			// der Anmelde-Server nicht rechtzeitig, gilt die Erneuerung als
			// gescheitert; der Balken sagt es, und die nächste erfolgreiche Anfrage
			// räumt ihn wieder weg, falls die Antwort doch noch kommt.
			await mitZeitgrenze(erneuerung, ZEITGRENZE.anmeldungErneuern, 'Anmeldung erneuern');
			return !!kc.token;
		} catch {
			return false;
		}
	}

	/**
	 * Frischen Schlüssel spiegeln, damit der Offline-Restore beim nächsten Start
	 * ihn nutzen kann.
	 */
	private spiegleSchluessel(kc: Keycloak): void {
		if (!kc.token) return;
		this.storage.setSync('kc-token', kc.token);
		if (kc.refreshToken) this.storage.setSync('kc-refresh-token', kc.refreshToken);
	}

	/**
	 * Verdrahtet `keycloak.onTokenExpired` einmalig. Keycloak feuert diesen
	 * Callback, sobald der Access-Token abläuft — auch nach langer aktiver Sitzung
	 * (im Hintergrund-Tab ggf. verzögert bis zum Fokus). Ohne diesen Hook
	 * degradierte die App still: Token/Rollen tot, aber kein Hinweis → leeres
	 * Rechte-Modal (User-Befund 2026-06-24). Nur der reauthInterceptor setzte das
	 * „Sitzung abgelaufen"-Banner, und nur bei einem echten API-401 — reine
	 * Permission-UI ohne Request blieb stumm.
	 */
	private wireTokenExpiry(): void {
		if (this.tokenExpiryWired) return;
		let kc: Keycloak | undefined;
		try { kc = this.authImpl.getKeycloak(); } catch { return; }
		if (!kc) return;
		this.tokenExpiryWired = true;
		// Bestehenden Handler (z.B. von keycloak-angular zur Event-Signal-Speisung)
		// NICHT verwerfen, sondern verketten — unsere Reaktion kommt zusätzlich obendrauf.
		const prev = kc.onTokenExpired;
		kc.onTokenExpired = () => {
			try { prev?.call(kc); } catch { /* fremder Handler darf uns nicht stören */ }
			void this.handleTokenExpired();
		};
	}

	/**
	 * Reaktion auf einen abgelaufenen Access-Token: still refreshen versuchen.
	 * Klappt es → Sitzung lebt weiter (evtl. „Sitzung abgelaufen"-Hinweis
	 * aufheben). Klappt es NICHT und wir sind online → die Sitzung ist echt tot
	 * (Refresh-Token abgelaufen / Silent-SSO blockiert): globales Banner setzen,
	 * damit der Nutzer den „Neu anmelden"-Knopf sieht, statt vor einem leeren
	 * Modal zu stehen. Offline wird NICHT gemeldet (Offline-First).
	 */
	async handleTokenExpired(): Promise<void> {
		const ok = await this.tryRefreshToken();
		if (ok) {
			this.serverBlock?.clearSession();
			return;
		}
		if (this.isOnline) this.serverBlock?.noteSessionExpired();
	}

	/**
	 * Holt die Online-Initialisierung nach, wenn die App im Offline-Modus
	 * gestartet ist (dann lief `keycloak.init()` nie → interner Adapter undefined)
	 * und der Nutzer später online geht. Ohne das blieb die App tokenlos (nur das
	 * gecachte Projekt sichtbar, kein „Sitzung abgelaufen"-Banner) und ein
	 * späterer Login-Klick stürzte ab.
	 *
	 * Idempotent: war keycloak bereits initialisiert, sind `init()` und
	 * `wireTokenExpiry()` No-ops; `checkAuthentication()` baut die Rollen aus dem
	 * (per reauth ggf. aufgefrischten) Token neu auf.
	 */
	private resumeOnline(): void {
		this.isOnline = true;
		void Promise.resolve(this.authImpl.init()).then(() => {
			this.wireTokenExpiry();
			this.checkAuthentication();
		});
	}

	/**
	 * Holt die Anmeldung nach — **bevor** etwas hochgeladen wird.
	 *
	 * Warum eigens (Nutzer-Befund 08.09.2026, Tablet): Nach tagelanger Arbeit im
	 * Offline-Modus ging nichts mehr zum Server. Am Gerät gemessen war kein
	 * Anmelde-Schlüssel da, alle Datenbank-Verbindungen standen auf dem
	 * öffentlichen Nur-Lese-Weg, und der Gateway wies jede Schreibanfrage mit
	 * `405` ab — ohne dass die App etwas sagte.
	 *
	 * Die App konnte das längst nachholen ({@link resumeOnline}), tat es aber zu
	 * spät: Das hängt an `offlineModeChanged`, und der Umschalter lädt ERST hoch
	 * und meldet DANN den Moduswechsel. Die Anmeldung kam also, nachdem das
	 * Hochladen schon gescheitert war. Zusätzlich stieg `tryRefreshToken()` gleich
	 * in der ersten Zeile aus, weil `isOnline` im Offline-Modus `false` ist.
	 *
	 * Deshalb hier ein Weg, der aus dem Offline-Modus heraus funktioniert: Erst
	 * den Anmelde-Unterbau hochfahren (nach einem Offline-Start lief
	 * `keycloak.init()` nie, der interne Adapter ist dann undefined), dann den
	 * Schlüssel holen. Idempotent — `init()` ist beim zweiten Mal ein No-Op, und
	 * der Abgleich ruft das bei jedem Versuch.
	 *
	 * Gibt ehrlich `false` zurück, wenn nichts zu holen war. Der Aufrufer
	 * entscheidet daran, ob er überhaupt zu übertragen versucht — lieber ein
	 * klarer Satz als ein stummes 405.
	 */
	async holeAnmeldungNach(): Promise<boolean> {
		// Wir werden gleich mit dem Server sprechen; ein „offline" aus dem
		// Offline-Modus darf die Erneuerung nicht von vornherein blockieren.
		this.isOnline = true;
		// Das Hochfahren des Anmelde-Unterbaus steckt seit 09.09.2026 in
		// `tryRefreshToken()` selbst — es wird auf jedem Weg gebraucht, nicht nur
		// vor dem Hochladen, und zwei Fassungen davon würden auseinanderlaufen.
		const frisch = await this.tryRefreshToken();
		if (frisch) this.checkAuthentication();
		return frisch;
	}

	private set(resource: string, access: string) {
		if (!resource || !access) return;
		if (!this.matrix.has(resource))
			this.matrix.set(resource, new Set<string>);
		this.matrix.get(resource)!.add(access);
	}
	
	public canI(access: string, resource: string) {
		if (!this.matrix.has(resource))
			return false;
		const res = this.matrix.get(resource);
		return res!.has(access);
	}
	
	/**
	 * @deprecated Wird auf `canIbe(role)` umgeleitet. Das Konzept einer
	 * "aktiven Rolle" wurde aufgegeben — ein User hat zu jeder Zeit alle
	 * seine Capabilities. Statt amI(role) bitte `canIbe(role)` verwenden.
	 */
	public amI(role: string) {
		return this.canIbe(role);
	}

	public canIbe(role: string) {
		if (this.roles.has(role)) return true;
		// `datennutzer` ist die Realm-Default-Rolle: sie liegt in den Realm-Rollen
		// (nicht in den bpweb-Client-Rollen `this.roles`). Jeder authentifizierte
		// User hat sie als read-only-Boden. Deshalb hier zusätzlich realmRoles
		// prüfen (User-Entscheidung Phase B). Hinweis: canIbe('datennutzer') ist
		// damit NICHT exklusiv ("nur Datennutzer") — dafür gibt es isDatennutzer().
		if (role === 'datennutzer' && this.realmRoles.has('datennutzer')) return true;
		return false;
	}

	/**
	 * Datennutzer = User hat ausschließlich den read-only-Boden (interne
	 * 'datennutzer'-Fallback-Rolle bzw. nur die Realm-Default-Rolle) oder darf
	 * keine Punkte lesen. Wird von UI-Komponenten + mask-Pipe genutzt, um
	 * sensible Felder zu verbergen.
	 */
	public isDatennutzer(): boolean {
		const onlyDatennutzer = this.roles.size === 1 && this.roles.has('datennutzer');
		return onlyDatennutzer || !this.canI('read', 'points');
	}

	/**
	 * @deprecated Phase B: 'gast' → 'datennutzer'. Alias auf `isDatennutzer()`,
	 * bis alle Aufrufer/Templates (auch in luzula-lib) migriert sind (Phase F).
	 */
	public isGuest(): boolean {
		return this.isDatennutzer();
	}

	canEditAndComment() {
		return (this.canIbe('kontrolleur') && this.canIbe('bearbeiter')) || this.canIbe('entwickler');
	}

	/**
	 * Wendet die aktuellen Rollen (`this.roles`) als Union-Capability-Matrix an
	 * und meldet den Login. Ersetzt das frühere `switchRole(bestRole)` — es gibt
	 * keine „aktive Rolle" mehr; `currentRole` bleibt leer (Interface-Kompat).
	 */
	private applyRoleCapabilities() {
		this.currentRole = '';
		this.matrix = this.buildRoleMatrix();
		this.storeAuthInfo();
		this.authReady.set(true);
		this.loggedIn.emit();
		// `commenting` wird NICHT automatisch gesetzt — das übernimmt die UI über
		// den PointEditModeService beim Öffnen eines Punkts.
		this.isAuthenticated.next(true);
	}

	getRoles() {
		return this.roles;
	}

	hasRealmRole(role: string): boolean {
		return this.realmRoles.has(role);
	}

	getRealmRoles() {
		return this.realmRoles;
	}

	logout(callback: any) {
		this.storage.removeSync('ai');
		this.storage.removeSync('kc-token');
		this.storage.removeSync('kc-refresh-token');

		this.currentUser = '';
		this.currentUsername = '';
		this.currentUserId = '';
		this.currentUserEmail = '';
		this.currentRole = '';
		this.roles.clear();
		this.realmRoles.clear();
		this.matrix = noPermissions;
		this.authReady.set(false);
		this.isAuthenticated.next(false);
		this.authImpl.notifyAuthenticated(false);

		if (callback) callback();

		// SSO-Session serverseitig beenden (Redirect zur Keycloak-Logout-Seite)
		if (this.isOnline) {
			this.authImpl.logout$().subscribe();
		}
	}

}
